import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Search, ArrowLeft, MapPin, Clock, Calendar } from 'lucide-react';

export const PartnerShowcase = () => {
  const { API } = useContext(AuthContext);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/partner-board`)
      .then((res) => setPosts((res.data.posts || []).filter((p) => p.status === 'active').slice(0, 3)))
      .catch(() => {});
  }, []);

  const timeAgo = (iso) => {
    const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 60) return `לפני ${Math.max(1, mins)} דק׳`;
    if (mins < 1440) return `לפני ${Math.floor(mins / 60)} שע׳`;
    return `לפני ${Math.floor(mins / 1440)} ימים`;
  };

  const fmtGameDate = (d) => {
    if (!d) return '';
    const dt = new Date(d + 'T00:00:00');
    return isNaN(dt.getTime()) ? d : dt.toLocaleDateString('he-IL', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  return (
    <div className="relative mt-12 overflow-hidden rounded-3xl shadow-2xl" data-testid="home-partners-section">
      <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-cyan-50 to-white"></div>
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-teal-300/30 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-cyan-300/30 rounded-full blur-3xl"></div>

      <div className="relative z-10 p-6 sm:p-10">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg">
              <Search className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-black text-gray-900">🎾 חיפוש פרטנרים למשחק</h2>
              <p className="text-gray-600 text-sm sm:text-base">מצאו שותף למשחק — פרסמו מודעה או הגיבו למודעה קיימת</p>
            </div>
          </div>
          <Link to="/partner-board" data-testid="home-partners-cta">
            <Button className="bg-gradient-to-l from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white rounded-full font-bold px-6 shadow-lg">
              לכל המודעות
              <ArrowLeft className="w-4 h-4 mr-2" />
            </Button>
          </Link>
        </div>

        {posts.length === 0 ? (
          <div className="mt-6 bg-white/70 backdrop-blur rounded-2xl p-6 text-center border border-teal-100">
            <span className="text-4xl block mb-2">🎾</span>
            <p className="font-bold text-gray-700">אין מודעות פעילות כרגע — פרסמו מודעה ומצאו שותף!</p>
            <Link to="/partner-board">
              <Button className="mt-3 rounded-full font-bold bg-gradient-to-l from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white" data-testid="home-partners-empty-cta">
                פרסמו מודעה ראשונה
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mt-6">
            {posts.map((p) => (
              <Link key={p.id} to="/partner-board" data-testid={`home-partner-card-${p.id}`}>
                <Card className="hover:shadow-xl hover:-translate-y-1 transition-all h-full border-teal-100">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Avatar className="w-9 h-9 border-2 border-teal-200">
                        {p.user_avatar && <AvatarImage src={p.user_avatar} alt={p.user_name} />}
                        <AvatarFallback className="bg-teal-100 text-teal-700 font-bold">{(p.user_name || '?').charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="font-bold text-gray-800 text-sm truncate">{p.user_name}</div>
                        <div className="text-[10px] text-gray-400">{timeAgo(p.created_at)}</div>
                      </div>
                      {p.level && <Badge variant="outline" className="mr-auto text-teal-700 border-teal-200 text-[10px]">{p.level}</Badge>}
                    </div>
                    <p className="text-sm text-gray-700 leading-snug line-clamp-2">{p.text}</p>
                    <div className="flex items-center gap-3 mt-2 text-[11px] text-gray-500 flex-wrap">
                      {p.game_date && <span className="flex items-center gap-0.5 font-bold text-purple-600"><Calendar className="w-3 h-3" />{fmtGameDate(p.game_date)}</span>}
                      {p.area && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3 text-teal-500" />{p.area}</span>}
                      {p.preferred_time && <span className="flex items-center gap-0.5"><Clock className="w-3 h-3 text-blue-500" />{p.preferred_time}</span>}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
